import { useCallback, useEffect, useMemo, useState } from 'react';
import documentsCacheStore, { buildCacheKey } from '../cache/documentsCacheStore';
import { buildDocumentsApiAdapter } from '../api/documentsApiAdapter';
import { isAllowedFileType, MAX_FILE_SIZE } from '../utils/fileValidation';

const showToast = (message) => {
  if (window.showtoast) {
    window.showtoast(message);
    return;
  }

  // eslint-disable-next-line no-alert
  alert(message);
};

const useCustomerDocuments = ({
  customerId,
  source,
  retailerScope,
  documentsEnabled,
  t,
  cacheStore = documentsCacheStore
}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingDocumentId, setDeletingDocumentId] = useState(null);

  const cacheKey = useMemo(
    () => buildCacheKey({ retailerScope, source, customerId }),
    [retailerScope, source, customerId]
  );

  const apiMethods = useMemo(
    () => buildDocumentsApiAdapter({ source, customerId }),
    [source, customerId]
  );

  const loadDocuments = useCallback(async ({ force = false, withLoading = true } = {}) => {
    if (!customerId || !documentsEnabled) return;

    if (!force) {
      const cachedDocuments = cacheStore.getEntry(cacheKey);
      if (cachedDocuments) {
        setDocuments(cachedDocuments);
        return;
      }
    }

    if (withLoading) setLoading(true);
    try {
      const response = await apiMethods.list();
      const fetchedDocuments = response?.documents || [];
      cacheStore.setEntry(cacheKey, fetchedDocuments);
      setDocuments(fetchedDocuments);
    } catch (error) {
      showToast(error?.message || t('customerDocuments.errors.load', { defaultValue: 'No se pudieron cargar los documentos.' }));
    } finally {
      if (withLoading) setLoading(false);
    }
  }, [apiMethods, cacheKey, cacheStore, customerId, documentsEnabled, t]);

  useEffect(() => {
    if (!documentsEnabled || !customerId) {
      setDocuments([]);
      return;
    }

    const cachedDocuments = cacheStore.getEntry(cacheKey);
    if (cachedDocuments) {
      setDocuments(cachedDocuments);
      return;
    }

    loadDocuments();
  }, [cacheKey, cacheStore, customerId, documentsEnabled, loadDocuments]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        loadDocuments({ withLoading: false });
      }
    };

    document.addEventListener('visibilitychange', refreshWhenVisible);
    window.addEventListener('focus', refreshWhenVisible);
    return () => {
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.removeEventListener('focus', refreshWhenVisible);
    };
  }, [loadDocuments]);

  const uploadDocument = useCallback(async (file) => {
    if (!file || !documentsEnabled) return;

    if (!isAllowedFileType(file)) {
      showToast(t('customerDocuments.errors.invalidType', { defaultValue: 'Tipo de archivo no permitido.' }));
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      showToast(t('customerDocuments.errors.maxSize', { defaultValue: 'El archivo supera el tamaño máximo permitido (25MB).' }));
      return;
    }

    setUploading(true);
    try {
      const response = await apiMethods.upload(file);
      const uploadedDocument = response?.document;

      if (uploadedDocument) {
        setDocuments((previousDocuments) => {
          const updatedDocuments = [
            uploadedDocument,
            ...previousDocuments.filter((document) => document.id !== uploadedDocument.id)
          ];
          cacheStore.setEntry(cacheKey, updatedDocuments);
          return updatedDocuments;
        });
      } else {
        cacheStore.invalidate(cacheKey);
        await loadDocuments({ force: true });
      }

      showToast(t('customerDocuments.success.uploaded', { defaultValue: 'Documento subido correctamente.' }));
    } catch (error) {
      showToast(error?.message || t('customerDocuments.errors.upload', { defaultValue: 'No se pudo subir el documento.' }));
    } finally {
      setUploading(false);
    }
  }, [apiMethods, cacheKey, cacheStore, documentsEnabled, loadDocuments, t]);

  const deleteDocument = useCallback(async (documentId) => {
    setDeletingDocumentId(documentId);
    try {
      await apiMethods.destroy(documentId);
      setDocuments((previousDocuments) => {
        const updatedDocuments = previousDocuments.filter((document) => document.id !== documentId);
        cacheStore.setEntry(cacheKey, updatedDocuments);
        return updatedDocuments;
      });
      showToast(t('customerDocuments.success.deleted', { defaultValue: 'Documento eliminado correctamente.' }));
    } catch (error) {
      showToast(error?.message || t('customerDocuments.errors.delete', { defaultValue: 'No se pudo eliminar el documento.' }));
    } finally {
      setDeletingDocumentId(null);
    }
  }, [apiMethods, cacheKey, cacheStore, t]);

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles?.length) {
      showToast(t('customerDocuments.errors.validation', { defaultValue: 'Revisa tipo y tamaño del archivo.' }));
    }

    if (!acceptedFiles?.length) return;
    await uploadDocument(acceptedFiles[0]);
  }, [t, uploadDocument]);

  return {
    documents,
    loading,
    uploading,
    deletingDocumentId,
    uploadDocument,
    deleteDocument,
    onDrop,
    reload: loadDocuments
  };
};

export default useCustomerDocuments;
